import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/admin/Ui";
import { Megaphone, Sparkle } from "@phosphor-icons/react";

const TYPES = [
  { v: "linkedin_post", l: "LinkedIn post" },
  { v: "newsletter", l: "Newsletter paragraph" },
  { v: "member_spotlight", l: "Member spotlight" },
  { v: "trade_lane_insight", l: "Trade lane insight" },
  { v: "event_announcement", l: "Event announcement" },
];

export default function Content() {
  const [form, setForm] = useState({ content_type: "linkedin_post", topic: "", additional_context: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/content");
    setItems(data.content || []);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Enter a topic"); return; }
    setBusy(true);
    try {
      await api.post("/content/generate", form);
      toast.success("Content generated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-content-page">
      <PageHeader title="Content Marketing" subtitle="Agent 8 · GPT-5-mini" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-black/10 p-6 space-y-4">
          <div>
            <div className="overline mb-2">Content type</div>
            <select
              data-testid="content-type-select"
              value={form.content_type}
              onChange={(e) => setForm({ ...form, content_type: e.target.value })}
              className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            >
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <div className="overline mb-2">Topic</div>
            <input
              data-testid="content-topic-input"
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. Port of Koper as the gateway to CEE"
              className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
          </div>
          <div>
            <div className="overline mb-2">Extra context</div>
            <textarea
              data-testid="content-context-input"
              rows={4}
              value={form.additional_context}
              onChange={(e) => setForm({ ...form, additional_context: e.target.value })}
              className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
          </div>
          <button
            data-testid="content-generate-button"
            onClick={generate}
            disabled={busy}
            className="btn-primary w-full justify-center"
          >
            <Sparkle size={14} /> {busy ? "Generating…" : "Generate content"}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {items.length === 0 ? <Empty label="No content yet." /> : items.map((c) => (
            <div key={c.id} className="bg-white border border-black/10 p-6" data-testid={`content-item-${c.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Megaphone size={16} weight="duotone" className="text-ember" />
                <span className="overline">{c.content_type.replace("_", " ")}</span>
              </div>
              {c.title && <h3 className="font-display font-bold text-xl text-navy">{c.title}</h3>}
              <p className="text-sm text-navy/80 whitespace-pre-wrap leading-relaxed mt-3">{c.content}</p>
              {c.hashtags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.hashtags.map((h, i) => (
                    <span key={i} className="text-xs text-ember">{h.startsWith("#") ? h : `#${h}`}</span>
                  ))}
                </div>
              )}
              {c.suggested_image && (
                <div className="mt-3 text-xs text-navy/50"><span className="font-semibold text-navy">Suggested image: </span>{c.suggested_image}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
